import type { BaseTokenInfo } from "@src/components/DefuseSDK/types/base"

const STABLECOIN_PREFERRED_SLIPPAGE_BASIS_POINTS = 1_000

type QuoteResult = { ok: unknown } | { err: unknown; correlationId?: string }

export async function getWithdrawQuoteWithDynamicSlippage<
  T extends QuoteResult,
>({
  tokenIn,
  tokenOut,
  maxSlippageBasisPoints,
  slippageExplicitlySet = false,
  getQuote,
  onAttemptFailed,
}: {
  tokenIn: BaseTokenInfo
  tokenOut: BaseTokenInfo
  maxSlippageBasisPoints: number
  // When the user has adjusted slippage themselves, honor their value instead
  // of the stablecoin default.
  slippageExplicitlySet?: boolean
  getQuote: (slippageBasisPoints: number) => Promise<T>
  onAttemptFailed?: (attempt: {
    slippageBasisPoints: number
    isStablecoinWithdrawal: boolean
    result: { err: unknown; correlationId?: string }
  }) => void
}): Promise<{ result: T; slippageBasisPoints: number }> {
  const isStablecoinWithdrawal = isStablecoinPair(tokenIn, tokenOut)
  const slippageBasisPoints =
    isStablecoinWithdrawal && !slippageExplicitlySet
      ? Math.min(
          maxSlippageBasisPoints,
          STABLECOIN_PREFERRED_SLIPPAGE_BASIS_POINTS
        )
      : maxSlippageBasisPoints
  const result = await getQuote(slippageBasisPoints)
  if (!("ok" in result)) {
    onAttemptFailed?.({ slippageBasisPoints, isStablecoinWithdrawal, result })
  }

  return { result, slippageBasisPoints }
}

export function isStablecoinPair(
  tokenIn: BaseTokenInfo,
  tokenOut: BaseTokenInfo
): boolean {
  return (
    tokenIn.tags?.includes("type:stablecoin") === true &&
    tokenOut.tags?.includes("type:stablecoin") === true
  )
}
